<script setup lang="ts">
import ConfirmActionDialog from "@/components/dialogs/ConfirmActionDialog.vue";
import DataTableFilterBanner from "@/components/shared/DataTableFilterBanner.vue";
import EntityActionButtons from "@/components/shared/EntityActionButtons.vue";
import EntityListTable from "@/components/shared/EntityListTable.vue";
import {
  EMPTY_STOCK_PRICE_STATS,
  GetStockPriceStats,
  GetStockPrices,
  type StockPriceStats,
} from "@/api/stockPriceApi";
import type { FrappeFilterDefinition } from "@/composables/useFrappeListFilters";
import { useStockPriceActions } from "@/composables/useStockPriceActions";
import { canDoctype } from "@/helper/permissionHelper";
import { useReportEngine } from "@/modules/reports/composables/useReportEngine";
import type { FrappeListFilterCondition } from "@/services/frappe/filters";
import type { StockPriceListItem } from "@/type/stockPrice";

/**
 * ⚠️ The permission key is an INLINE LITERAL and must stay one. `definePage` meta
 * is hoisted out of component scope at compile time, so a `PAGE_KEYS.…` reference
 * here throws "PAGE_KEYS is not defined" at runtime.
 */
definePage({ meta: { permissionKey: "page.warehouse.stock_prices" } });

const STOCK_PRICE_DOCTYPE = "Stock Price";

const { t } = useI18n();
const router = useRouter();

const stats = ref<StockPriceStats>({ ...EMPTY_STOCK_PRICE_STATS });

const canCreate = computed(() => canDoctype(STOCK_PRICE_DOCTYPE, "create"));

interface StockPriceFilterState {
  search: string;
  status: string | null;
  conditions: FrappeListFilterCondition[];
}

/**
 * ⚠️ Returns `undefined` for anything unrecognised, and that is the whole point.
 *
 * The obvious ternary sends every unknown value — an empty select included — to
 * `0`, so clearing the status filter would quietly narrow the list to drafts
 * while looking unfiltered. `useFrappeListFilters` drops a binding whose value is
 * `undefined`, so the clause simply never exists.
 */
function statusToDocstatus(value: unknown) {
  if (value === "Submitted")
    return 1;
  if (value === "Cancelled")
    return 2;
  if (value === "Draft")
    return 0;

  return undefined;
}

/**
 * ⚠️ NO CURRENCY COLUMN. `currency` cannot be requested in a list query on this
 * site — see the note in `stockPriceApi.ts`. Adding it back fails the whole query
 * and empties the page. The currency is shown on the document itself.
 */
const headers = computed(() => [
  { title: t("alkokh.stockPrice.columns.document"), key: "document" },
  { title: t("alkokh.global.status"), key: "status" },
  { title: t("alkokh.stockPrice.columns.sellingList"), key: "sellingPriceList" },
  { title: t("alkokh.stockPrice.columns.buyingList"), key: "buyingPriceList" },
  { title: t("alkokh.global.actions"), key: "actions", sortable: false },
]);

const statusOptions = computed(() => [
  { label: t("alkokh.stockPrice.status.draft"), value: "Draft" },
  { label: t("alkokh.stockPrice.status.submitted"), value: "Submitted" },
  { label: t("alkokh.stockPrice.status.cancelled"), value: "Cancelled" },
]);

const filterDefinitions = computed<FrappeFilterDefinition<StockPriceFilterState>[]>(() => [
  {
    // Hidden because the banner renders the condition builder itself.
    key: "conditions",
    type: "conditions",
    hidden: true,
  },
  {
    key: "status",
    type: "select",
    label: t("alkokh.stockPrice.filters.status"),
    placeholder: t("alkokh.stockPrice.filters.allStatuses"),
    icon: "tabler-flag",
    options: statusOptions.value,
    doctype: STOCK_PRICE_DOCTYPE,
    field: "docstatus",
    operator: "=",
    transform: statusToDocstatus,
    enabled: value => statusToDocstatus(value) !== undefined,
  },
]);

/**
 * What the condition builder may filter on: only fields this list can actually
 * SHOW, plus the standard audit ones.
 *
 * ⚠️ No posting date here, deliberately. Whether this doctype carries one is not
 * confirmed (see `STOCK_PRICE_FIELD_MAP`), and offering a filter on a field that
 * may not exist would fail the whole query and render an empty register.
 */
const conditionAllowedFields = [
  "name",
  "selling_price_list",
  "buying_price_list",
  "docstatus",
  "owner",
  "creation",
  "modified",
];

const conditionFieldLabelOverrides = computed(() => ({
  name: t("alkokh.stockPrice.columns.document"),
  selling_price_list: t("alkokh.stockPrice.columns.sellingList"),
  buying_price_list: t("alkokh.stockPrice.columns.buyingList"),
  docstatus: t("alkokh.global.status"),
}));

/**
 * Column key -> the field the backend can actually order by.
 *
 * ⚠️ Not a tidy-up. Interpolating a Vuetify column key straight into `order_by`
 * produces an invalid sort for every column whose key is not also a real database
 * column; the request fails and the list renders empty — so sorting looks like
 * "there are no price updates".
 */
const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  document: "name",
  status: "docstatus",
  sellingPriceList: "selling_price_list",
  buyingPriceList: "buying_price_list",
};

function resolveSortField(key?: string) {
  return (key && SORT_FIELD_BY_COLUMN[key]) || "modified";
}

function resolveStatusLabel(value: string) {
  return statusOptions.value.find(option => option.value === value)?.label || value || "—";
}

function resolveStatusColor(status: string) {
  if (status === "Submitted")
    return "success";
  if (status === "Cancelled")
    return "error";

  return "warning";
}

const {
  items,
  total,
  loading,
  searchQuery,
  filterValues,
  pagination,
  tableSortBy,
  fetchItems,
  updateOptions,
  resetState,
  loadError,
} = useReportEngine<StockPriceListItem, StockPriceFilterState>({
  fetcher: async ({ search, filters, orFilters, page, itemsPerPage, sortBy, orderBy }) => {
    // One query object for both calls, so the tiles can never count a different
    // set than the table lists.
    const query = { search, filters, orFilters };

    const [result, statsResult] = await Promise.all([
      GetStockPrices(ref(false), {
        ...query,
        limit_start: (page - 1) * itemsPerPage,
        limit_page_length: itemsPerPage,
        order_by: `${resolveSortField(sortBy)} ${orderBy || "desc"}`,
      }),
      GetStockPriceStats(ref(false), query),
    ]);

    stats.value = statsResult ?? { ...EMPTY_STOCK_PRICE_STATS };

    return result;
  },
  filterDefinitions,
  initialFilterValues: {
    status: null,
    conditions: [],
  },
  initialPageSize: 10,
  pageSizeOptions: [10, 20, 50, 100],
  initialSortBy: { key: "document", order: "desc" },
  persist: { key: "warehouse-stock-prices", allowedConditionFields: conditionAllowedFields },
});

const hasActiveFilters = computed(() =>
  Boolean(filterValues.value?.status || filterValues.value?.conditions?.length));

/**
 * ⚠️ Cancelled earns a tile of its own, unlike the stock-count register where it
 * would be noise. A cancelled price update means prices were rolled back — which
 * is usually the exact thing someone opens this page to find.
 */
const summaryCards = computed(() => [
  { label: t("alkokh.stockPrice.metrics.total"), value: String(total.value), color: "secondary" },
  { label: t("alkokh.stockPrice.metrics.draft"), value: String(stats.value.draft), color: "warning" },
  { label: t("alkokh.stockPrice.metrics.submitted"), value: String(stats.value.submitted), color: "success" },
  { label: t("alkokh.stockPrice.metrics.cancelled"), value: String(stats.value.cancelled), color: "error" },
]);

function updateFilters(value: Record<string, unknown>) {
  filterValues.value = value as Omit<StockPriceFilterState, "search">;
}

function openView(item: StockPriceListItem) {
  router.push(`/warehouse/stock-prices/${item.id}`);
}

function openEdit(item: StockPriceListItem) {
  router.push({ path: `/warehouse/stock-prices/${item.id}`, query: { mode: "edit" } });
}

const actions = useStockPriceActions({
  onChanged: async () => {
    await fetchItems();
  },
  onDeleted: async () => {
    await fetchItems();
  },
});
</script>

<template>
  <div class="stock-price-page d-flex flex-column gap-6">
    <VRow>
      <VCol
        v-for="card in summaryCards"
        :key="card.label"
        cols="12"
        sm="6"
        xl="3"
      >
        <VCard
          variant="outlined"
          class="stock-price-page__metric-card"
        >
          <VCardText>
            <div class="text-body-2 text-medium-emphasis mb-2">
              {{ card.label }}
            </div>
            <div
              class="text-h4 font-weight-semibold"
              :class="`text-${card.color}`"
            >
              {{ card.value }}
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <div class="d-flex align-center justify-space-between flex-wrap gap-3">
      <div>
        <h1 class="text-h4 font-weight-semibold">
          {{ t("alkokh.stockPrice.title") }}
        </h1>
        <div class="text-body-2 text-medium-emphasis mt-1">
          {{ t("alkokh.stockPrice.subtitle") }}
        </div>
      </div>

      <VBtn
        v-if="canCreate"
        color="primary"
        prepend-icon="tabler-plus"
        to="/warehouse/stock-prices/create"
      >
        {{ t("alkokh.stockPrice.createAction") }}
      </VBtn>
    </div>

    <DataTableFilterBanner
      :search="searchQuery"
      :model-value="filterValues"
      :filter-definitions="filterDefinitions"
      :doctype="STOCK_PRICE_DOCTYPE"
      :condition-allowed-fields="conditionAllowedFields"
      :condition-field-label-overrides="conditionFieldLabelOverrides"
      :search-placeholder="t('alkokh.stockPrice.searchPlaceholder')"
      :loading="loading"
      @update:search="searchQuery = $event"
      @update:model-value="updateFilters"
      @reset="resetState"
      @refresh="fetchItems"
    />

    <!--
      ⚠️ A REFUSED QUERY MUST NEVER READ AS AN EMPTY REGISTER. Without this the
      table falls through to its "no price updates yet" empty state, which is a
      false statement about the business rather than a report about the request —
      and it is exactly how this page first shipped looking empty against a site
      that had documents. `loadError` carries the backend's own message when there
      is one.
    -->
    <VAlert
      v-if="loadError"
      type="error"
      variant="tonal"
    >
      <template #title>
        {{ t("alkokh.stockPrice.loadFailed") }}
      </template>

      <div v-if="loadError !== 'load-failed'">
        {{ loadError }}
      </div>

      <VBtn
        class="mt-2"
        size="small"
        variant="tonal"
        :loading="loading"
        @click="fetchItems"
      >
        {{ t("alkokh.stockPrice.retry") }}
      </VBtn>
    </VAlert>

    <VCard
      variant="outlined"
      class="stock-price-page__table-card"
    >
      <EntityListTable
        :headers="headers"
        :items="items"
        :items-length="total"
        :loading="loading"
        :pagination="pagination"
        :fetch-items="fetchItems"
        :sort-by="tableSortBy"
        item-value="id"
        :filtered="Boolean(searchQuery) || hasActiveFilters"
        :no-data-text="t('alkokh.stockPrice.empty')"
        :empty-action-label="canCreate ? t('alkokh.stockPrice.createAction') : ''"
        @update:options="updateOptions"
        @empty-action="router.push('/warehouse/stock-prices/create')"
        @clear-filters="resetState"
      >
        <template #item.document="{ item }">
          <div class="d-flex flex-column py-2">
            <span class="font-weight-medium">{{ item.id }}</span>
          </div>
        </template>

        <template #item.status="{ item }">
          <VChip
            size="small"
            variant="outlined"
            class="stock-price-page__chip"
            :color="resolveStatusColor(item.status)"
          >
            {{ resolveStatusLabel(item.status) }}
          </VChip>
        </template>

        <template #item.sellingPriceList="{ item }">
          <span>{{ item.sellingPriceList || "—" }}</span>
        </template>

        <template #item.buyingPriceList="{ item }">
          <span>{{ item.buyingPriceList || "—" }}</span>
        </template>

        <!--
          ⚠️ NO SUBMIT BUTTON HERE, deliberately. Applying a price update is only
          safe to authorise after reading the Action column, and on documents that
          create an item's first selling price it requires an explicit
          acknowledgement. Neither is visible from a list row, so submit lives on
          the document itself. Cancel stays, because its dialog carries the whole
          warning in one sentence.
        -->
        <template #item.actions="{ item }">
          <div class="d-flex align-center gap-1 py-2">
            <EntityActionButtons
              :show-view="true"
              :show-edit="actions.canEdit(item)"
              :show-delete="actions.canDelete(item)"
              @view="openView(item)"
              @edit="openEdit(item)"
              @delete="actions.askDelete(item)"
            />

            <VBtn
              v-if="actions.canCancel(item)"
              icon="tabler-arrow-back-up"
              variant="text"
              size="small"
              color="error"
              :aria-label="t('alkokh.stockPrice.cancelDialog.confirm')"
              @click="actions.askCancel(item)"
            />
          </div>
        </template>
      </EntityListTable>
    </VCard>

    <ConfirmActionDialog
      v-model:is-dialog-visible="actions.cancelDialogVisible.value"
      :title="t('alkokh.stockPrice.cancelDialog.title')"
      :message="`${t('alkokh.stockPrice.cancelDialog.message')} ${t('alkokh.stockPrice.cancelDialog.warning')}`"
      :confirm-btn="t('alkokh.stockPrice.cancelDialog.confirm')"
      confirm-color="error"
      icon="tabler-arrow-back-up"
      :loading="actions.actionLoading.value"
      @confirm="actions.cancelStockPrice()"
    />

    <ConfirmActionDialog
      v-model:is-dialog-visible="actions.deleteDialogVisible.value"
      :title="t('alkokh.stockPrice.deleteDialog.title')"
      :message="t('alkokh.stockPrice.deleteDialog.message')"
      :confirm-btn="t('alkokh.stockPrice.deleteDialog.confirm')"
      confirm-color="error"
      icon="tabler-trash"
      :loading="actions.actionLoading.value"
      @confirm="actions.deleteDraft()"
    />
  </div>
</template>

<style scoped>
.stock-price-page {
  background: transparent;
}

.stock-price-page__metric-card,
.stock-price-page__table-card,
.stock-price-page__chip {
  background: rgb(var(--v-theme-surface));
}
</style>
